import { isThisWeek } from 'date-fns';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval } from '../../../../../../constants';
import { SalesItem, useStoreInformation, useStoreOpeningHours } from '../../../../../../hooks';
import { useCurrencyTools } from '../../../../../../hooks/useCurrencyTools';
import { Container, Typography } from '../../../../../../stories/atoms';
import { ColumnFlexContainer } from '../../../../../../stories/atoms/containerV2/ContainerV2';
import { HeaderRowItem, Table } from '../../../../../../stories/molecules/tablev2/Table';
import { commonTranslations, homeTranslations } from '../../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../../utils';
import { InfoTooltipIcon } from '../../../../common/infoTooltipIcon/InfoTooltipIcon';
import { isMonthlyView, isWeeklyView } from '../../../../util';
import { DailyColumnType, WeekDayOrder, WeeklyColumnType } from '../util';

type Metrics =
  | 'sales'
  | 'salesByTotal'
  | 'customers'
  | 'customersByTotal'
  | 'salesByCustomer'
  | 'itemsSold'
  | 'itemsSoldByCustomer';

const currencyMetrics = ['sales', 'salesByCustomer'];

type TransformedData = {
  name: Metrics;
  [key: string]: number | Metrics;
};

/** This function calculates the total of a metric across all time units */
const calculateTotal = (item: TransformedData) => {
  return Object.entries(item)
    .filter(([key]) => key !== 'name')
    .reduce((sum, [, value]) => sum + (typeof value === 'number' ? value : 0), 0);
};

export type SalesInsightTableProps = {
  tableData: SalesItem[];
  saleWithoutReturns: number;
  currentDate: Date;
  interval: StorePerformanceInterval;
};

export const SalesInsightTable: FC<SalesInsightTableProps> = ({
  tableData,
  currentDate,
  saleWithoutReturns,
  interval,
}) => {
  const { t } = useTranslation();
  const { getStoreOpeningHours } = useStoreOpeningHours();
  const { countryCode } = useStoreInformation();
  const { storeOpeningHour, storeClosingHour } = getStoreOpeningHours(currentDate);
  const { mon, tue, wed, thu, fri, sat, sun, total, metric } = commonTranslations;
  const { selectedCurrency, getFormattedCurrency } = useCurrencyTools(interval, currentDate);

  const translationMap = {
    mon,
    tue,
    wed,
    thu,
    fri,
    sat,
    sun,
  };
  const {
    salesInsightWidget: {
      metrics: metricsTranslations,
      metricTooltips: metricTooltipTranslations,
      includeVatText,
    },
  } = homeTranslations;

  /** Filters daily columns based on store opening hours */
  const filterDailyColumns = (): DailyColumnType[] => {
    return Object.values(DailyColumnType).filter((value) => {
      const hour = Number.parseInt(value);
      return hour >= storeOpeningHour && hour <= storeClosingHour;
    }) as DailyColumnType[];
  };

  /** Filters weekly columns based on days */
  const filterWeeklyColumns = (weekDate: Date): WeeklyColumnType[] => {
    const today = new Date();

    const isCurrentWeek = isThisWeek(weekDate, { weekStartsOn: 0 }); // Sunday-start week

    if (isCurrentWeek) {
      const todayDayIndex = today.getDay(); // 0 (Sun) to 6 (Sat)
      return WeekDayOrder.slice(0, todayDayIndex + 1);
    }

    // Full week if not current
    return WeekDayOrder;
  };

  const getMonthlyHeaders = () => {
    return tableData.map((item) => item.timeUnit);
  };

  /** Generates header items dynamically */
  const getHeaderItems = useCallback((): HeaderRowItem<DailyColumnType | WeeklyColumnType | string>[] => {
    const isWeekly = isWeeklyView(interval);
    const isMonthly = isMonthlyView(interval);
    const columns = isMonthly
      ? getMonthlyHeaders()
      : isWeekly
      ? filterWeeklyColumns(currentDate)
      : filterDailyColumns();

    return [
      {
        id: DailyColumnType.Metrics,
        content: <Typography type="body3">{t(metric.key, metric.defaultValue)}</Typography>,
      },
      ...columns.map((col) => ({
        id: col,
        content: (
          <Typography type="body3">
            {isWeekly
              ? t(translationMap[col as keyof typeof translationMap].key, {
                  defaultValue: translationMap[col as keyof typeof translationMap].defaultValue,
                })
              : col}
          </Typography>
        ),
      })),
      {
        id: DailyColumnType.Total,
        content: <Typography type="body3">{t(total.key, total.defaultValue)}</Typography>,
      },
    ];
  }, [currentDate]);

  const headerItems = getHeaderItems();

  /** Transforms table data to match the required structure */
  const transformData = (): TransformedData[] =>
    (
      [
        'sales',
        'salesByTotal',
        'customers',
        'customersByTotal',
        'salesByCustomer',
        'itemsSold',
        'itemsSoldByCustomer',
      ] as Metrics[]
    ).map((metricName) => {
      const transformed: TransformedData = { name: metricName };
      tableData.forEach(({ timeUnit, [metricName]: value }) => {
        const key = timeUnit;
        if (key) transformed[key] = value;
      });

      return transformed;
    });

  /** Helper to calculate the total of a metric across all time units */
  const getTotalForMetric = (data: TransformedData[], metricName: Metrics): number => {
    const item = data.find((d) => d.name === metricName);
    if (!item) return 0;

    return calculateTotal(item);
  };

  const transformed = transformData();

  const totalSales = getTotalForMetric(transformed, 'sales');
  const totalCustomer = getTotalForMetric(transformed, 'customers');
  const totalItemsSold = getTotalForMetric(transformed, 'itemsSold');

  // Calculate total for each row
  const getTotal = (item: TransformedData): string => {
    let calculatedValue;
    if (item.name === 'salesByCustomer') {
      calculatedValue = saleWithoutReturns > 0 ? totalSales / saleWithoutReturns : 0;
    } else if (item.name === 'itemsSoldByCustomer') {
      calculatedValue = totalCustomer > 0 ? totalItemsSold / totalCustomer : 0;
    } else {
      calculatedValue = calculateTotal(item);
    }

    const convertedTotalValue = currencyMetrics.includes(item.name)
      ? getFormattedCurrency(calculatedValue, { showCurrencyCode: false })
      : formatAmountWithSeparator(calculatedValue);

    return convertedTotalValue;
  };

  const isNorthAmericanRegion = countryCode === 'US' || countryCode === 'CA' || countryCode === 'PR';

  const getMetricTooltipText = (metricName: Metrics): string => {
    const baseTooltipText = t(metricTooltipTranslations[metricName].key, {
      defaultValue: metricTooltipTranslations[metricName].defaultValue,
    });

    if (metricName === 'sales' && isNorthAmericanRegion) {
      return `${baseTooltipText} ${t(includeVatText.key, includeVatText.defaultValue)}`;
    }

    return baseTooltipText;
  };

  return (
    <ColumnFlexContainer flex={1} minHeight={[0]} data-testid="sales-insights-table-wrapper">
      <Table
        data-testid="sales-insights-table"
        items={transformData() || []}
        headerItems={headerItems}
        stickyHeader
        cellSize="small"
        stickyFirstColumn
        stickyLastColumn
        firstCellMinWidth="160px"
        getRowItems={(item) => {
          const row = Object.keys(item)
            .filter((key) => key !== 'name')
            .map((key) => ({
              id: key as DailyColumnType | WeeklyColumnType,
              content: (
                <Typography>
                  {currencyMetrics.includes(item.name)
                    ? getFormattedCurrency(item[key] as number, { showCurrencyCode: false })
                    : formatAmountWithSeparator(item[key] as number)}
                </Typography>
              ),
            }));

          return [
            {
              id: DailyColumnType.Metrics,
              width: '15%',
              content: (
                <Container wrap="nowrap" direction="horizontal">
                  <Typography type="body3">
                    {t(metricsTranslations[item.name].key, {
                      defaultValue: metricsTranslations[item.name].defaultValue,
                      currency: selectedCurrency,
                    })}
                  </Typography>
                  <InfoTooltipIcon
                    tooltipText={getMetricTooltipText(item.name)}
                    testId={`sales-insights-${item.name}-info-icon`}
                  />
                </Container>
              ),
            },
            ...row,
            {
              id: DailyColumnType.Total,
              content: <Typography>{getTotal(item)}</Typography>,
            },
          ];
        }}
      />
    </ColumnFlexContainer>
  );
};
